import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import {
  deleteEmployeePhotoApi,
  getEmployeePhotoBlobApi,
  uploadEmployeePhotoApi,
  EMPLOYEE_PHOTO_MAX_BYTES,
  EMPLOYEE_PHOTO_TYPES,
} from "../api/employeesApi";

const initialsOf = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

type Props = {
  token: string;
  employeeId: string;
  fullName: string;
  /** Muda a cada troca de foto — é o que dispara a nova busca. */
  photoAssetId?: string | null;
  /** Chamado depois de enviar/remover, para a ficha recarregar. */
  onChanged: () => void;
};

/**
 * Foto do colaborador na ficha do DP.
 *
 * A imagem fica no armazenamento de uploads, mas é lida SÓ pela rota do DP
 * (`GET /dp/employees/:id/photo`, ADMIN e DP) — nunca pelo `/uploads/images`,
 * que supervisor e freelancer também alcançam. Como <img src> não manda o token,
 * a imagem vem como blob e vira object URL (revogado ao trocar ou desmontar).
 */
export default function EmployeePhoto({ token, employeeId, fullName, photoAssetId, onChanged }: Props) {
  const [src, setSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!photoAssetId) {
      setSrc(null);
      return;
    }

    let url: string | null = null;
    let cancelled = false;

    getEmployeePhotoBlobApi(token, employeeId)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => !cancelled && setSrc(null));

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [token, employeeId, photoAssetId]);

  const handleFile = async (file?: File) => {
    if (!file) return;
    if (!EMPLOYEE_PHOTO_TYPES.includes(file.type)) {
      toast.error("Envie uma imagem JPG, PNG ou WEBP.");
      return;
    }
    if (file.size > EMPLOYEE_PHOTO_MAX_BYTES) {
      toast.error("A foto pode ter no máximo 10 MB.");
      return;
    }

    setBusy(true);
    try {
      await uploadEmployeePhotoApi(token, employeeId, file);
      toast.success("Foto atualizada.");
      onChanged();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao enviar a foto.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleRemove = async () => {
    if (!window.confirm("Remover a foto do colaborador?")) return;
    setBusy(true);
    try {
      await deleteEmployeePhotoApi(token, employeeId);
      toast.success("Foto removida.");
      onChanged();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao remover a foto.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dpPhoto">
      <div className="dpPhoto__frame">
        {src ? (
          <img className="dpPhoto__img" src={src} alt={`Foto de ${fullName}`} />
        ) : (
          <span className="dpPhoto__initials" aria-hidden="true">
            {initialsOf(fullName)}
          </span>
        )}
      </div>

      <div className="dpPhoto__actions">
        <input
          ref={inputRef}
          type="file"
          accept={EMPLOYEE_PHOTO_TYPES.join(",")}
          style={{ display: "none" }}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <button
          type="button"
          className="dpBtn dpBtn--secondary dpBtn--small"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
        >
          {busy ? "Enviando..." : photoAssetId ? "Trocar foto" : "Enviar foto"}
        </button>
        {photoAssetId ? (
          <button
            type="button"
            className="dpBtn dpBtn--danger dpBtn--small"
            onClick={handleRemove}
            disabled={busy}
          >
            Remover
          </button>
        ) : null}
      </div>
    </div>
  );
}
